import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  Eye,
  X,
  AlertCircle,
  Users,
  Calendar,
  Shield,
} from 'lucide-react';

const DEFAULT_TERMS_CONTENT = `PRACTICE MANAGER PRO — TERMS AND CONDITIONS

Last updated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}

1. ACCEPTANCE OF TERMS

By accessing or using Practice Manager Pro ("the Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you must not use the Service.

The Service is provided by Practice Manager Pro Pty Ltd (ABN to be confirmed) ("we", "us", "our").

2. DEFINITIONS

"Account" means your registered account to access the Service.
"Authorised User" means any individual granted access to the Service under your Account.
"Client Data" means all data you or your Authorised Users enter, upload, or store in the Service.
"Firm" means the accounting practice or business entity that holds the Account.
"Financial Year" means the period from 1 July to 30 June, consistent with the Australian financial year.

3. ACCOUNT REGISTRATION AND ACCESS

3.1 You must provide accurate, current and complete information during registration.
3.2 You are responsible for maintaining the confidentiality of your login credentials.
3.3 You must notify us immediately of any unauthorised use of your Account.
3.4 Each Authorised User must have their own unique login credentials.
3.5 You are responsible for all activities conducted under your Account.

4. SUBSCRIPTION AND PAYMENT

4.1 The Service is offered on a subscription basis with a 30-day free trial.
4.2 After the trial period, you must select a paid plan to continue using the Service.
4.3 Subscription fees are billed in advance on a monthly or annual basis as selected.
4.4 All prices are in Australian Dollars (AUD) and are exclusive of GST unless stated otherwise.
4.5 We reserve the right to change pricing with 30 days' written notice.
4.6 Failure to pay may result in suspension or termination of your Account.

5. DATA PROTECTION AND PRIVACY

5.1 We comply with the Australian Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
5.2 Client Data remains your property at all times.
5.3 We will not access your Client Data except as necessary to provide the Service or as required by law.
5.4 All data is stored in Australian data centres (Sydney region).
5.5 Tax File Numbers (TFNs) are encrypted using AES-256 encryption at rest and in transit.
5.6 We implement industry-standard security measures including encryption, access controls, and regular security audits.
5.7 In the event of a data breach, we will notify you and the Office of the Australian Information Commissioner (OAIC) as required under the Notifiable Data Breaches (NDB) scheme.

6. ACCEPTABLE USE

6.1 You must use the Service only for lawful purposes and in accordance with these Terms.
6.2 You must not:
    (a) Use the Service to store or transmit malicious code;
    (b) Attempt to gain unauthorised access to any part of the Service;
    (c) Use the Service in a way that could damage, disable, or impair the Service;
    (d) Reverse engineer, decompile, or disassemble any part of the Service;
    (e) Use the Service to send unsolicited communications in breach of the Spam Act 2003 (Cth);
    (f) Resell or sublicense the Service without our prior written consent.

7. INTELLECTUAL PROPERTY

7.1 We retain all intellectual property rights in the Service, including software, design, and documentation.
7.2 You retain all intellectual property rights in your Client Data.
7.3 You grant us a limited licence to use your Client Data solely for the purpose of providing the Service.

8. INTEGRATIONS AND THIRD-PARTY SERVICES

8.1 The Service may integrate with third-party services including the Australian Taxation Office (ATO), Xero Practice Manager (XPM), and Microsoft 365.
8.2 Your use of third-party integrations is subject to the respective third-party terms.
8.3 We are not responsible for the availability or performance of third-party services.
8.4 You authorise us to access third-party services on your behalf when you enable integrations.

8A. THIRD-PARTY DATA PROCESSING

8A.1 To provide the Service, your Client Data may be processed by the following third-party service providers:
     (a) Cloudflare, Inc. — Application delivery, security, and request processing. Cloudflare processes requests at its global edge network but does not store Client Data at rest.
     (b) Neon, Inc. — Database hosting. All Client Data is stored in Neon PostgreSQL databases located in Sydney, Australia (ap-southeast-2 region). Data is encrypted at rest and in transit.
     (c) Amazon Web Services (AWS) — Transactional email delivery via Amazon SES (Sydney region). Email content is processed in transit only and not stored.
     (d) Microsoft Corporation — When you enable Microsoft 365 integration, we access your Microsoft account data (email, calendar) using OAuth 2.0 authorisation that you explicitly grant. Tokens are encrypted at rest.
     (e) Xero Limited — When you enable Xero Practice Manager integration, we access your XPM data (clients, jobs, staff, timesheets) using OAuth 2.0 authorisation that you explicitly grant. Tokens are encrypted at rest using AES-256-GCM.
8A.2 We require all third-party service providers to maintain appropriate security standards.
8A.3 We will not share your Client Data with any third party other than as described in this section, except as required by law.
8A.4 You may request a current list of sub-processors at any time by contacting us.

9. SERVICE AVAILABILITY

9.1 We aim to provide 99.9% uptime for the Service.
9.2 We may perform scheduled maintenance with reasonable prior notice.
9.3 We are not liable for downtime caused by factors outside our reasonable control.

10. LIMITATION OF LIABILITY

10.1 To the maximum extent permitted by Australian Consumer Law, our liability is limited to:
     (a) Resupplying the Service; or
     (b) Refunding the subscription fees paid in the preceding 12 months.
10.2 We are not liable for any indirect, incidental, special, or consequential damages.
10.3 Nothing in these Terms excludes or limits liability that cannot be excluded under Australian Consumer Law.

11. TERMINATION

11.1 You may cancel your subscription at any time through your Account settings.
11.2 We may suspend or terminate your Account if you breach these Terms.
11.3 Upon termination:
     (a) Your access to the Service will cease;
     (b) You will have 30 days to export your Client Data;
     (c) After 90 days, your Client Data will be permanently deleted.

12. DISPUTE RESOLUTION

12.1 Any dispute arising from these Terms will be governed by the laws of New South Wales, Australia.
12.2 The parties agree to attempt to resolve disputes through mediation before commencing legal proceedings.
12.3 The courts of New South Wales, Australia have exclusive jurisdiction.

13. CHANGES TO TERMS

13.1 We may update these Terms from time to time.
13.2 We will notify you of material changes at least 30 days before they take effect.
13.3 Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.

14. CONTACT

For questions about these Terms, contact us at:
Email: legal@practicemanagerpro.com.au
Address: Sydney, NSW, Australia`;

export default function TermsConditionsPage() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [previewTerm, setPreviewTerm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    version: '',
    title: 'Terms and Conditions',
    content: '',
    effective_date: '',
    is_active: false,
  });

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getTerms();
      setTerms(response.data.terms || []);
    } catch (err) {
      setError('Failed to load terms and conditions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTerm(null);
    setFormData({
      version: generateNextVersion(),
      title: 'Terms and Conditions',
      content: DEFAULT_TERMS_CONTENT,
      effective_date: new Date().toISOString().split('T')[0],
      is_active: terms.length === 0,
    });
    setShowEditor(true);
  };

  const handleEdit = (term) => {
    setEditingTerm(term);
    setFormData({
      version: term.version,
      title: term.title,
      content: term.content,
      effective_date: term.effective_date?.split('T')[0] || '',
      is_active: term.is_active,
    });
    setShowEditor(true);
  };

  const handlePreview = (term) => {
    setPreviewTerm(term);
    setShowPreview(true);
  };

  const handleSave = async () => {
    if (!formData.version || !formData.content || !formData.effective_date) {
      setError('Version, content, and effective date are required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingTerm) {
        await adminApi.updateTerm(editingTerm.id, formData);
      } else {
        await adminApi.createTerm(formData);
      }
      setShowEditor(false);
      await fetchTerms();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save terms');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (term) => {
    if (!window.confirm(`Delete version ${term.version}? This cannot be undone.`)) return;

    try {
      await adminApi.deleteTerm(term.id);
      await fetchTerms();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete terms');
    }
  };

  const handleActivate = async (term) => {
    try {
      await adminApi.activateTerm(term.id);
      await fetchTerms();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to activate terms');
    }
  };

  const generateNextVersion = () => {
    if (terms.length === 0) return '1.0';
    const versions = terms.map(t => parseFloat(t.version) || 0);
    const maxVersion = Math.max(...versions);
    return (maxVersion + 0.1).toFixed(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Terms & Conditions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage the terms and conditions shown to users during signup.
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Version
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Terms List */}
      {terms.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Terms & Conditions</h3>
          <p className="text-gray-500 mb-6">
            Create your first version to display during the signup process.
          </p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Terms & Conditions
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Effective Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acceptances
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {terms.map((term) => (
                <tr key={term.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">v{term.version}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{term.title}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1.5" />
                      {formatDate(term.effective_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {term.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="w-4 h-4 mr-1.5" />
                      {term.acceptance_count || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(term.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handlePreview(term)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(term)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {!term.is_active && (
                        <>
                          <button
                            onClick={() => handleActivate(term)}
                            className="p-1.5 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50"
                            title="Set as Active"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(term)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowEditor(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingTerm ? `Edit Version ${editingTerm.version}` : 'New Terms & Conditions'}
                </h2>
                <button
                  onClick={() => setShowEditor(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-8rem)] space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                    <input
                      type="text"
                      value={formData.version}
                      onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g. 1.0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Terms and Conditions"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
                    <input
                      type="date"
                      value={formData.effective_date}
                      onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    Set as active version (displayed during signup)
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={20}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter terms and conditions content..."
                  />
                </div>
              </div>

              <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 space-x-3">
                <button
                  onClick={() => setShowEditor(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingTerm ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewTerm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowPreview(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {previewTerm.title} (v{previewTerm.version})
                  </h2>
                  <p className="text-sm text-gray-500">
                    Effective: {formatDate(previewTerm.effective_date)}
                    {previewTerm.is_active && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                    {previewTerm.content}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
